// =========================
// Unified Invoice Service
// =========================

import { withLayoutProtection } from './layoutProtection';
import { generateHTMLToPDF } from './htmlToPdfService';

export interface UnifiedInvoiceData {
  invoiceNumber: string;
  billedFrom: {
    companyName: string;
    address: string;
    email: string;
    gstNumber: string;
  };
  billedTo: {
    customerName: string;
    clubName: string;
    teamName?: string; // Add team name field
    address: string | null;
    email: string | null;
    gstNumber: string | null;
  };
  invoiceDate: string;
  issuedOn: string;
  items: Array<{
    description: string;
    tournamentName: string;
    teamName: string;
    ageCategory: string;
    playersCount: number;
    transactionId: string;
    totalAmount: number;
  }>;
  summary: {
    subtotal: number;
    igst: number;
    total: number;
  };
  paymentInstructions: string[];
  gstId: string;
  additionalNotes: string;
}

/**
 * Generate unified invoice data from any transaction type
 * @param transaction - Transaction data (any type)
 * @returns UnifiedInvoiceData
 */
export const generateUnifiedInvoiceData = (transaction: any): UnifiedInvoiceData => {
  const now = new Date();
  const invoiceNumber = `INV-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  
  // Calculate base amount excluding 18% IGST
  const total = transaction.amount || 0; // Total amount including IGST
  const baseAmount = Math.round((total / 1.18) * 100) / 100; // Base amount excluding IGST
  const igst = Math.round((baseAmount * 0.18) * 100) / 100; // IGST amount (18%)

  // Handle different transaction structures
  const tournamentName = transaction.tournamentInfo?.name || transaction.tournament?.name || 'Tournament';
  const teamName = transaction.teamInfo?.name || transaction.team?.name || 'Team';
  // Fix: Check teamInfo.ageCategory first for super-admin payments, then fallback to tournament age category
  const ageCategory = transaction.teamInfo?.ageCategory || transaction.tournamentInfo?.ageCategory || transaction.tournament?.ageCategory;
  const playersCount = transaction.playersCount || transaction.players?.length || 1;
  const transactionId = transaction.paymentId || transaction._id || 'N/A';
  
  // Handle club info from different structures - FIXED: Properly extract club name
  const clubInfo = transaction.teamInfo?.clubInfo || transaction.clubInfo || transaction.club || {};
  const clubName = clubInfo.name || clubInfo.clubName || transaction.teamInfo?.clubName || transaction.team?.clubName || 'Club';
  const clubAddress = clubInfo.address || null;
  const clubEmail = clubInfo.email || transaction.teamInfo?.email || transaction.team?.email || null;
  const clubGst = clubInfo.gstNum || clubInfo.gstNumber || null; // Support both gstNum and gstNumber

  return {
    invoiceNumber,
    billedFrom: {
      companyName: "Jambavan Academy Private Limited",
      address: "Villa 183, Adarsh Palm Retreat, Lane 8, Bellandur, Bengaluru, Karnataka 560103",
      email: "contact@sportsforlife.in",
      gstNumber: "29ABCDE1234F1Z5"
    },
    billedTo: {
      customerName: clubName, // FIXED: Use club name as customer name
      clubName: clubName, // FIXED: Use actual club name
      teamName: teamName, // Add team name
      address: clubAddress,
      email: clubEmail,
      gstNumber: clubGst
    },
    invoiceDate: now.toLocaleDateString('en-GB'),
    issuedOn: now.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    items: [{
      description: `${tournamentName} - ${teamName} - ${ageCategory || 'N/A'}`,
      tournamentName: tournamentName,
      teamName: teamName,
      ageCategory: ageCategory || 'N/A',
      playersCount,
      transactionId,
      totalAmount: Math.round(baseAmount * 100) / 100
    }],
    summary: {
      subtotal: Math.round(baseAmount * 100) / 100,
      igst: Math.round(igst * 100) / 100,
      total: Math.round(total * 100) / 100
    },
    paymentInstructions: [
      "(i) 18% IGST will be applicable on all invoices",
      "(ii) For Delhi Based clients, GST must be split equally between Center and State and reflected on the invoice"
    ],
    gstId: "29ABCDE1234F1Z5",
    additionalNotes: "Fixture will be shared once approved"
  };
};

/**
 * Helper function to format currency
 * @param amount - Amount to format
 * @returns Formatted currency string
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Helper function to get logo as base64
 * @returns Promise<string>
 */
const getLogoBase64 = async (): Promise<string> => {
  try {
    // Import the logo dynamically
    const InvoiceLogo = await import('../assets/images/admin-logos/Invoice-logo.png');
    const response = await fetch(InvoiceLogo.default);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting logo to base64:', error);
    // Fallback to text-based logo if image fails
    return '';
  }
};

/**
 * Download invoice as PDF using the same working method as Invoice Modal
 * @param transaction - Transaction data (any type)
 * @returns Promise<void>
 */
export const downloadUnifiedInvoice = async (transaction: any): Promise<void> => {
  return withLayoutProtection(async () => {
    try {
      console.log('Generating unified invoice data for transaction:', transaction._id);
      const invoiceData = generateUnifiedInvoiceData(transaction);
      console.log('Unified invoice data generated:', invoiceData);
      
      console.log('Starting unified PDF generation using working method...');
      
      // Use HTML-to-PDF for exact UI replica
      const pdfBlob = await generateHTMLToPDF(invoiceData);
      console.log('PDF generated successfully using HTML-to-PDF, blob size:', pdfBlob.size);
      
      // Create filename
      const tournamentName = (transaction.tournamentInfo?.name || transaction.tournament?.name || 'Tournament').replace(/\s+/g, '-');
      const teamName = (transaction.teamInfo?.name || transaction.team?.name || 'Team').replace(/\s+/g, '-');
      const filename = `invoice-${tournamentName}-${teamName}.pdf`;
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      console.log('Downloading file:', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download initiated successfully');
    } catch (error) {
      console.error('Error downloading unified invoice:', error);
      throw new Error('Failed to generate PDF invoice');
    }
  });
};

/**
 * Generate PDF blob using the same working method as Invoice Modal
 * @param invoiceData - Invoice data
 * @returns Promise<Blob>
 */
const generateUnifiedPDFBlob = async (invoiceData: UnifiedInvoiceData): Promise<Blob> => {
  try {
    console.log('Starting PDF generation with off-screen method...');
    
    // Get logo as base64
    const logoBase64 = await getLogoBase64();
    
    // Create a completely off-screen canvas for PDF generation
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    // Set canvas dimensions for A4 PDF
    const pdfWidth = 800;
    const pdfHeight = 1200; // Approximate A4 height
    canvas.width = pdfWidth;
    canvas.height = pdfHeight;
    
    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, pdfWidth, pdfHeight);
    
    // Set font styles
    ctx.font = '16px Arial, sans-serif';
    ctx.fillStyle = '#333333';
    
    let yPosition = 50; // Starting Y position
    
    // Draw logo or fallback
    if (logoBase64) {
      const logoImg = new Image();
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = logoBase64;
      });
      
      // Draw logo at top left
      ctx.drawImage(logoImg, 50, yPosition, 120, 80);
      yPosition += 120; // Move down after logo
    } else {
      // Draw fallback SFL logo
      ctx.fillStyle = '#0077F9';
      ctx.fillRect(50, yPosition, 120, 80);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('SFL', 110, yPosition + 50);
      ctx.textAlign = 'left';
      yPosition += 120;
    }
    
    // Draw header line
    ctx.strokeStyle = '#f8f9fa';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(50, yPosition);
    ctx.lineTo(pdfWidth - 50, yPosition);
    ctx.stroke();
    
    yPosition += 40;
    
    // Draw billing sections - FIXED: Proper alignment
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillStyle = '#495057';
    ctx.fillText('Billed From:', 50, yPosition);
    
    // FIXED: Properly align "Billed To" section
    const billedToX = pdfWidth / 2 + 20; // Center-right alignment
    ctx.fillText('Billed To:', billedToX, yPosition);
    yPosition += 25;
    
    // Draw "Billed From" content
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#0077F9';
    ctx.fillText(invoiceData.billedFrom.companyName, 50, yPosition);
    yPosition += 20;
    ctx.fillStyle = '#333333';
    ctx.fillText(invoiceData.billedFrom.address, 50, yPosition);
    yPosition += 20;
    ctx.fillStyle = '#0077F9';
    ctx.fillText(invoiceData.billedFrom.email, 50, yPosition);
    yPosition += 20;
    ctx.fillStyle = '#666666';
    ctx.fillText(`GST ID: ${invoiceData.billedFrom.gstNumber}`, 50, yPosition);
    
    // Draw "Billed To" content - FIXED: Use fixed Y position to avoid clubbing
    const billedToStartY = 160; // Fixed starting position for Billed To
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#0077F9';
    ctx.fillText(invoiceData.billedTo.clubName, billedToX, billedToStartY);
    
    // Add team name if available
    if (invoiceData.billedTo.teamName) {
      ctx.font = '13px Arial, sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText(`Team: ${invoiceData.billedTo.teamName}`, billedToX, billedToStartY + 20);
    }
    
    if (invoiceData.billedTo.address) {
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = '#333333';
      ctx.fillText(invoiceData.billedTo.address, billedToX, billedToStartY + 40);
    }
    
    if (invoiceData.billedTo.gstNumber) {
      ctx.font = '13px Arial, sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText(`GST ID: ${invoiceData.billedTo.gstNumber}`, billedToX, billedToStartY + 60);
    }
    
    yPosition += 40;
    
    // Draw issued on date
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#666666';
    ctx.textAlign = 'right';
    ctx.fillText(`Issued on: ${invoiceData.issuedOn}`, pdfWidth - 50, yPosition);
    ctx.textAlign = 'left';
    yPosition += 40;
    
    // Draw invoice table - FIXED: Better column spacing to avoid clubbing
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(50, yPosition, pdfWidth - 100, 40);
    ctx.fillStyle = '#495057';
    ctx.fillText('ITEM', 70, yPosition + 25);
    ctx.fillText('TRANSACTION ID', 400, yPosition + 25); // Moved further right
    ctx.fillText('TOTAL AMOUNT', 650, yPosition + 25); // Moved further right
    yPosition += 50;
    
    // Draw table rows
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#333333';
    
    invoiceData.items.forEach((item, index) => {
      const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
      ctx.fillStyle = bgColor;
      ctx.fillRect(50, yPosition, pdfWidth - 100, 40);
      
      ctx.fillStyle = '#333333';
      ctx.fillText(item.description, 70, yPosition + 25);
      ctx.fillText(item.transactionId, 400, yPosition + 25); // Match new position
      ctx.fillText(formatCurrency(item.totalAmount), 650, yPosition + 25); // Match new position
      yPosition += 50;
    });
    
    yPosition += 20;
    
    // Draw summary table - FIXED: Wider table to prevent truncation
    const summaryWidth = 400; // Increased width
    const summaryX = pdfWidth - summaryWidth - 50;
    
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#495057';
    ctx.fillText('ITEM', summaryX + 20, yPosition + 25);
    ctx.fillText('AMOUNT', summaryX + 300, yPosition + 25); // Increased spacing
    yPosition += 50;
    
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#333333';
    ctx.fillText('Club Name:', summaryX + 20, yPosition + 25); // FIXED: Show club name instead of team name
    ctx.fillText(invoiceData.billedTo.clubName, summaryX + 300, yPosition + 25); // Updated spacing
    yPosition += 50;
    
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#333333';
    ctx.fillText('Team Name:', summaryX + 20, yPosition + 25); // FIXED: Show team name separately
    ctx.fillText(invoiceData.billedTo.teamName || 'Team', summaryX + 300, yPosition + 25); // Use teamName from billedTo
    yPosition += 50;
    
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#333333';
    ctx.fillText('Subtotal:', summaryX + 20, yPosition + 25);
    ctx.fillText(formatCurrency(invoiceData.summary.subtotal), summaryX + 300, yPosition + 25); // Updated spacing
    yPosition += 50;
    
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#333333';
    ctx.fillText('IGST(18%):', summaryX + 20, yPosition + 25);
    ctx.fillText(formatCurrency(invoiceData.summary.igst), summaryX + 300, yPosition + 25); // Updated spacing
    yPosition += 50;
    
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(summaryX, yPosition, summaryWidth, 40);
    ctx.fillStyle = '#0077F9';
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillText('Total:', summaryX + 20, yPosition + 25);
    ctx.fillText(formatCurrency(invoiceData.summary.total), summaryX + 300, yPosition + 25); // Updated spacing
    
    console.log('Canvas-based PDF generation completed successfully');
    
    // Convert canvas to PDF using jsPDF
    const { default: jsPDF } = await import('jspdf');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgData = canvas.toDataURL('image/png');
    const pdfWidth_mm = pdf.internal.pageSize.getWidth();
    const pdfHeight_mm = (canvas.height * pdfWidth_mm) / canvas.width;
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth_mm, pdfHeight_mm);
    
    console.log('PDF created successfully');
    
    // Convert PDF to blob
    const pdfBlob = pdf.output('blob');
    console.log('PDF converted to blob successfully');
    
    return pdfBlob;
    
  } catch (error) {
    console.error('Error generating unified PDF blob:', error);
    throw new Error('Failed to generate PDF blob');
  }
};

/**
 * Generate PDF blob for email attachment
 * @param transaction - Transaction data (any type)
 * @returns Promise<Blob>
 */
export const generateUnifiedInvoiceBlob = async (transaction: any): Promise<Blob> => {
  try {
    console.log('Generating unified invoice blob for transaction:', transaction._id);
    const invoiceData = generateUnifiedInvoiceData(transaction);
    
    // Generate PDF blob using working method
    const pdfBlob = await generateUnifiedPDFBlob(invoiceData);
    console.log('PDF blob generated successfully, size:', pdfBlob.size);
    
    return pdfBlob;
  } catch (error) {
    console.error('Error generating unified invoice blob:', error);
    throw new Error('Failed to generate PDF blob for email');
  }
};

/**
 * Get email recipient from transaction
 * @param transaction - Transaction data (any type)
 * @returns string | null
 */
export const getInvoiceEmailRecipient = (transaction: any): string | null => {
  // Try to get email from different possible locations
  return transaction.clubInfo?.email || 
         transaction.teamInfo?.email || 
         transaction.team?.email || 
         transaction.club?.email || 
         null;
};

/**
 * Generate email subject for invoice
 * @param transaction - Transaction data (any type)
 * @returns string
 */
export const generateInvoiceEmailSubject = (transaction: any): string => {
  const tournamentName = transaction.tournamentInfo?.name || transaction.tournament?.name || 'Tournament';
  const teamName = transaction.teamInfo?.name || transaction.team?.name || 'Team';
  return `Invoice for ${tournamentName} - ${teamName}`;
};

/**
 * Generate email body for invoice
 * @param transaction - Transaction data (any type)
 * @returns string
 */
export const generateInvoiceEmailBody = (transaction: any): string => {
  const tournamentName = transaction.tournamentInfo?.name || transaction.tournament?.name || 'Tournament';
  const teamName = transaction.teamInfo?.name || transaction.team?.name || 'Team';
  
  return `Dear Team,

Please find attached the invoice for ${tournamentName} - ${teamName}.

Invoice Details:
- Tournament: ${tournamentName}
- Team: ${teamName}
- Amount: ₹${transaction.amount || 0}
- Status: ${transaction.paidStatus || 'Pending'}

The PDF invoice is attached to this email.

Best regards,
SFL Team`;
};
